import axios from 'axios';
import { toUploadProxyUrl } from '@/shared/lib/utils';
import type { UploadToS3Params, UploadToS3WithFieldsParams } from './types';

/**
 * S3にファイルをアップロード（Presigned PUT方式）
 *
 * presigned URLを使用してファイルをS3にアップロードします。
 * バックエンドのpresigned URL署名に含まれるヘッダー（Content-Type, Content-Disposition）を
 * 送信する必要があります。
 *
 * NOTE: ファイル名エンコードはバックエンド（Python urllib.parse.quote）と同じ結果になるよう
 * encodeURIComponent を使用しています。
 *
 * @param params - アップロードパラメータ
 * @param params.presignedUrl - S3の presigned URL
 * @param params.file - アップロードするファイル
 * @param params.contentType - Content-Type（省略時はファイルのMIMEタイプを使用）
 * @returns Axiosレスポンス
 *
 * @example
 * ```typescript
 * const file = new File(['content'], 'test.csv', { type: 'text/csv' });
 * await uploadToS3({
 *   presignedUrl: 'https://s3.amazonaws.com/...',
 *   file,
 * });
 * ```
 *
 * @throws {AxiosError} アップロードに失敗した場合
 */
export const uploadToS3 = async ({ presignedUrl, file, contentType }: UploadToS3Params) => {
  // Python の urllib.parse.quote と同等の処理
  const encodedFileName = encodeURIComponent(file.name);
  // LGWAN閉域網ではS3に直接アクセスできないため、CloudFront経由でプロキシする
  const uploadUrl = toUploadProxyUrl(presignedUrl);

  return await axios.put(uploadUrl, file, {
    headers: {
      'Content-Type': contentType ?? file.type,
      'Content-Disposition': `filename*=UTF-8''${encodedFileName}`,
    },
  });
};

/**
 * S3にファイルをアップロード（Presigned POST方式）
 *
 * Presigned POSTを使用してファイルをS3にアップロードします。
 * この方式ではサーバー側でファイルサイズ制限を強制できます。
 *
 * NOTE: fieldsは必ずfileより前に追加する必要があります（S3の仕様）。
 *
 * @param params - アップロードパラメータ
 * @param params.presignedUrl - S3の presigned POST URL
 * @param params.file - アップロードするファイル
 * @param params.fields - POSTリクエストに含めるフィールド（policy, signature等）
 * @returns Axiosレスポンス
 *
 * @example
 * ```typescript
 * const file = new File(['content'], 'test.pdf', { type: 'application/pdf' });
 * await uploadToS3WithFields({
 *   presignedUrl: 'https://s3.amazonaws.com/bucket',
 *   file,
 *   fields: { key: '...', policy: '...', 'x-amz-signature': '...' },
 * });
 * ```
 *
 * @throws {AxiosError} アップロードに失敗した場合（ファイルサイズ超過時はEntityTooLargeエラー）
 */
export const uploadToS3WithFields = async ({
  presignedUrl,
  file,
  fields,
}: UploadToS3WithFieldsParams) => {
  const formData = new FormData();

  // fieldsを先に追加（順序重要：S3の仕様でfileは最後）
  for (const [key, value] of Object.entries(fields)) {
    formData.append(key, value);
  }

  // fileは最後に追加
  formData.append('file', file);

  // LGWAN閉域網ではS3に直接アクセスできないため、CloudFront経由でプロキシする
  const uploadUrl = toUploadProxyUrl(presignedUrl);

  return await axios.post(uploadUrl, formData);
};
